import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
// import PersonOffOutlined from "@mui/icons-material/PersonOffOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import WorkOffOutlinedIcon from "@mui/icons-material/WorkOffOutlined";
import {
  // Avatar,
  Box,
  Collapse,
  Grid2,
  Skeleton,
  Typography,
} from "@mui/material";
import { format, isPast, isSameDay, parseISO } from "date-fns";
import { ConfirmProvider } from "material-ui-confirm";
import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { defaultConfirmOptions } from "../../../../config/confirm-config";
import { hasPermission } from "../../../../utils/helpers";
import { getFullTimeShiftsMeta } from "../../store/full-time-shifts-slice";
import { ShiftDialog } from "./shift-dialog";
import TruncatedText from "./truncated-text";
import { useShiftVisibilityObserver } from "./use-shift-visibility-observer";

const BORDER_COLOR = "#DEDEDE";
const DYNAMIC_BORDER_BACKGROUND = false;

interface Workcenter {
  id: number;
  name: string;
  locationId: number;
}

interface ShiftDetail {
  shiftId: string;
  startDateTime: string;
  endDateTime: string;
  locationId: number;
  workcenterId: number;
  shiftPatternId: number;
  shiftPatternName: string; // Added for display purposes
}

interface ShiftMeta {
  shiftId: string;
  workcenterId: number;
  requiredWorkers: number;
  availableWorkers: number;
  jobs: JobInfo[];
  missingWorkers: string[];
}

type JobStatus = "OS" | "US";

interface JobInfo {
  jobId: number;
  status: JobStatus;
  value: number;
}

interface WorkcenterRow {
  workcenterId: number;
  workcenterName: string;
  dailyShifts: {
    [dayIndex: number]: ShiftDetail[];
  };
}

const groupShiftsByPatternAcrossDays = (dailyShifts: Record<number, ShiftDetail[]>) => {
  const patternMap: Record<string, { pattern: string; shiftsByDay: Record<number, ShiftDetail> }> = {};

  for (let day = 0; day < 7; day++) {
    for (const shift of dailyShifts[day] ?? []) {
      const key = shift.shiftPatternName;
      if (!patternMap[key]) {
        patternMap[key] = { pattern: key, shiftsByDay: {} };
      }
      patternMap[key].shiftsByDay[day] = shift;
    }
  }

  return patternMap;
};

// Generates 7-day view per workcenter
const groupShiftsByWorkcenterAndDay = (
  workcenters: Workcenter[],
  shifts: ShiftDetail[],
  startDate: Date
): WorkcenterRow[] => {
  return workcenters.map((wc) => {
    const wcShifts = shifts.filter((s) => s.workcenterId === wc.id);

    const dailyShifts: Record<number, ShiftDetail[]> = {};
    Array.from({ length: 7 }).forEach((_, i) => {
      dailyShifts[i] = [];
    });

    wcShifts.forEach((shift) => {
      const shiftDate = parseISO(shift.startDateTime);
      const dayIndex = isSameDay(shiftDate, startDate)
        ? 0
        : Math.floor((+shiftDate - +startDate) / (1000 * 60 * 60 * 24));
      if (dayIndex >= 0 && dayIndex < 7) {
        dailyShifts[dayIndex].push(shift);
      }
    });

    return {
      workcenterId: wc.id,
      workcenterName: wc.name,
      dailyShifts,
    };
  });
};

const ShiftCard = ({
  shift,
  shiftMeta,
  jobsMap,
  workcentersMap,
  workcenterName,
  onVisible,
  observeOnMount = true,
  hoveredShift,
  setHoveredShift
}: {
  shift: ShiftDetail;
  shiftMeta?: ShiftMeta;
  jobsMap?: Record<number, string>;
  workcentersMap?: Record<number, string>;
  workcenterName?: string;
  onVisible: (meta: { shiftId: string; workcenterId: number }) => void;
  observeOnMount: boolean;
  hoveredShift: {
    patternName: string;
    shiftId: string;
  } | null;
  setHoveredShift: Dispatch<SetStateAction<{
    patternName: string;
    shiftId: string;
  } | null>>
}) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement | null>(null);
  const getFullTimeShiftsMetaStatus = useSelector(
    (state: any) => state.fullTimeShifts.getFullTimeShiftsMeta.status
  );
  const locations = useSelector(
    (state: any) => state.fullTimeShifts.filterData.allLocations
  );
  const userRolesWithPermission = useSelector((state: any) => state.user.userData.userRolesWithPermissions);
  const canOpenFullTimeShiftDialog = useMemo(() => {
    return hasPermission(userRolesWithPermission, "FULL_TIME_SCHEDULER.VIEW_FULL_TIME_SHIFT_DETAILS");
  }, [userRolesWithPermission]);
  const start = format(parseISO(shift.startDateTime), "h:mm a");
  const fullStart = format(
    parseISO(shift.startDateTime),
    "M/d/yyyy, h:mm a"
  );
  const end = format(parseISO(shift.endDateTime), "h:mm a");
  const missingWorkers = shiftMeta?.missingWorkers ?? [];
  // Sorting Understaffed first then Overstaffed, highest value first
  const jobs = [...shiftMeta?.jobs ?? []]?.sort(
    (a, b) => {
      if (a.status === "US" && b.status === "OS") return -1; // US first
      if (a.status === "OS" && b.status === "US") return 1; // OS after US
      return b.value - a.value; // Sort by value within same status
    }
  ) ?? [];
  const availableWorkers = shiftMeta?.availableWorkers ?? 0;
  const requiredWorkers = shiftMeta?.requiredWorkers ?? 0;

  const isHovered = hoveredShift?.patternName === shift.shiftPatternName;
  const isThisCardHovered = hoveredShift?.shiftId === shift.shiftId;

  const pastShift = parseISO(shift.startDateTime) < new Date();
  const baseBackground = pastShift ? "rgba(0,0,0,0.02)" : "#fff";

  const backgroundColor = isThisCardHovered
    ? "#F7F9FC" // very light blueish-gray
    : isHovered
      ? pastShift ? "rgba(0,0,0,0.02)"
      :  DYNAMIC_BORDER_BACKGROUND ?
          availableWorkers < requiredWorkers ?
            "#D32F2F0A" :
            availableWorkers > requiredWorkers
              ? "#FFFEFB" : baseBackground
          : baseBackground
      : baseBackground;

  const borderColor = isThisCardHovered
    ? "#B0B0B0"
    : isHovered
      ? "rgba(0,0,0,0.3)"
      : DYNAMIC_BORDER_BACKGROUND ? availableWorkers < requiredWorkers
        ? "#E44B48"
        : availableWorkers > requiredWorkers
          ? "#FBC02D" : BORDER_COLOR : BORDER_COLOR;

  const { observe } = useShiftVisibilityObserver(onVisible);

  useEffect(() => {
    if (ref.current && observeOnMount) {
      observe(ref.current);
    }
  }, [observe, observeOnMount]);

  const [openDialog, setOpenDialog] = useState(false);

  const getLocationName = (locationId: number | undefined): string => {
    if (!locationId || !locations) return t("full-time-shifts_unknown-location");
    const location = locations.find((loc: any) => loc.id === locationId);
    return location ? location.name : t("full-time-shifts_unknown-location");
  };

  const [showPopout, setShowPopout] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setShowPopout(true);
    }, 500); // delay in ms
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setShowPopout(false);
  };

  return (
    <>
      <Box
        ref={ref}
        data-shift-id={shift?.shiftId}
        data-workcenter-id={shift?.workcenterId}
      >
        <Box
          onMouseEnter={() => setHoveredShift?.({ patternName: shift.shiftPatternName, shiftId: shift.shiftId })}
          onMouseLeave={() => setHoveredShift?.(null)}
          onClick={() => {
            if (canOpenFullTimeShiftDialog) {
              setOpenDialog(true);
            }
          }}
          sx={{
            border: `1px solid ${borderColor}`,
            borderRadius: "8px",
            p: 1,
            backgroundColor: backgroundColor,
            cursor: "pointer",
            minHeight: "90px",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto",
              fontSize: "16px",
              fontWeight: 500,
              lineHeight: "24px",
              color: "rgba(0,0,0,0.87)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
            }}
          >
            {shift.shiftPatternName}
          </Typography>
          <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
            <ScheduleIcon sx={{ fontSize: 16 }} />
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontSize: "14px",
                fontWeight: 400,
                lineHeight: "20px",
                color: "rgba(0,0,0,0.87)",
              }}
            >
              {start} - {end}
            </Typography>
          </Box>
          {
            shiftMeta?.availableWorkers !== undefined
            || shiftMeta?.requiredWorkers !== undefined ? (
            <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
              <GroupOutlinedIcon sx={{ fontSize: 16 }} />
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontSize: "12px",
                  fontWeight: 400,
                  lineHeight: "20px",
                  color:
                    availableWorkers < requiredWorkers
                      ? "#E44B48"
                      : "rgba(0,0,0,0.87)",
                }}
              >
                {shiftMeta?.availableWorkers || 0} /{" "}
                {shiftMeta?.requiredWorkers || 0}
              </Typography>
            </Box>
          ) : (
            <></>
          )}
          {!(!shiftMeta?.requiredWorkers &&
            !shiftMeta?.availableWorkers &&
            getFullTimeShiftsMetaStatus === "pending") && jobs.length > 0 && (
            <Box
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              display="flex"
              alignItems="flex-start"
              gap={0.5}
              mt={0.5}
              sx={{
                position: "relative",
                maxWidth: "100%",
                // "&:hover .hover-popout": {
                //   display: "flex",
                // },
                // "&:hover .job-container": {
                //   overflow: "visible",
                // },
                // "&:hover .more-jobs-count": {
                //   display: "none",
                // },
                // "&:hover .first-job": {
                //   display: "none",
                // },
            }}
            >
              <WorkOffOutlinedIcon sx={{ fontSize: 16, mt: "4px" }} />
              <Box
                className="job-container"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "nowrap",
                  gap: "4px",
                  overflow: showPopout ? "visible" : "hidden",
                  position: "relative",
                  maxWidth: "100%",
                  minHeight: "28px",
                }}
              >
                {/* Main visible content (first job + +X more) */}
                <Box
                  className="first-job"
                  display={showPopout ? "none" : "flex"}
                  alignItems="center"
                  borderRadius="100px"
                  px={0.5}
                  py={0.5}
                  bgcolor="#F3F3F3"
                  maxWidth="100%"
                  overflow="hidden"
                  sx={{
                    boxShadow: "inset 0 1px 1px rgba(255,255,255,0.6), 0 1px 2px rgba(0,0,0,0.05)",
                  }}
                >
                  <TruncatedText text={jobsMap?.[jobs[0].jobId] || t("full-time-shifts_unknown-job")} tooltipDisabled={true} />
                  <Box
                    sx={{
                      ml: "4px",
                      px: "6px",
                      py: "1px",
                      borderRadius: "64px",
                      backgroundColor:
                        jobs[0].status === "OS" ? "#FFE5B9" : "#F9DEDC",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: "13px",
                        lineHeight: "18px",
                        color: "rgba(0,0,0,0.87)",
                      }}
                    >
                      {jobs[0].value}
                    </Typography>
                  </Box>
                </Box>

                {jobs.length > 1 && (
                  <Typography
                    display={showPopout ? "none" : "flex"}
                    className="more-jobs-count"
                    sx={{
                      fontSize: "12px",
                      fontFamily: "Roboto",
                      color: "rgba(0,0,0,0.6)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("full-time-shifts_plus-count-more", { count: jobs.length - 1 })}
                  </Typography>
                )}

                {/* Hover popout with all jobs */}
                {showPopout && (
                  <Box
                    className="hover-popout"
                    sx={{
                      flexDirection: "column",
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      backgroundColor: "rgba(255, 255, 255, 0.25)",
                      backdropFilter: "blur(20px) saturate(180%)",
                      WebkitBackdropFilter: "blur(20px) saturate(180%)",
                      border: "1px solid rgba(255, 255, 255, 0.4)",
                      borderRadius: "16px",
                      boxShadow: `
                      inset 0 1px 2px rgba(255, 255, 255, 0.6),
                      inset 0 -1px 2px rgba(255, 255, 255, 0.2),
                      0 8px 32px rgba(0, 0, 0, 0.1)`,
                      marginTop: "-35px",
                      marginLeft: "-27px",
                      zIndex: 1000,
                      padding: "6px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Box gap={0.5} sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "flex-start",
                      justifyContent: "flex-start",
                    }}>
                      <WorkOffOutlinedIcon sx={{ fontSize: 16, mt: "4px" }} />
                      <Box sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        justifyContent: "flex-start",
                      }}>
                        {jobs.map((job, idx) => (
                          <Box
                            key={idx}
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            marginTop={idx === 0 ? 0 : 0.5}
                            borderRadius="100px"
                            px={0.5}
                            py={0.5}
                            bgcolor="#F3F3F3"
                            width="fit-content"
                            sx={{
                              boxShadow: "inset 0 1px 1px rgba(255,255,255,0.6), 0 1px 2px rgba(0,0,0,0.05)",
                            }}
                          >
                            <TruncatedText text={jobsMap?.[job.jobId] || t("full-time-shifts_unknown-job")} tooltipDisabled={true} />
                            <Box
                              sx={{
                                px: "6px",
                                py: "1px",
                                borderRadius: "64px",
                                backgroundColor:
                                  job.status === "OS" ? "#FFE5B9" : "#F9DEDC",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontFamily: "Roboto",
                                  fontWeight: 400,
                                  fontSize: "13px",
                                  lineHeight: "18px",
                                  color: "rgba(0,0,0,0.87)",
                                }}
                              >
                                {job.value}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}
          {(!shiftMeta?.requiredWorkers &&
            !shiftMeta?.availableWorkers &&
            getFullTimeShiftsMetaStatus === "pending") && (
              <Skeleton
                variant="text"
                width={100}
                height={20}
                sx={{ mt: 0.5 }}
              />
          )}
          {/* Getting wrong data will enable once that is fixed for 25.7 release */}
          {/*{missingWorkers.length > 0 && (*/}
          {/*  <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>*/}
          {/*    <PersonOffOutlined sx={{ fontSize: 16 }} />*/}
          {/*    <Box*/}
          {/*      sx={{*/}
          {/*        display: "flex",*/}
          {/*        alignItems: "center",*/}
          {/*        position: "relative",*/}
          {/*      }}*/}
          {/*    >*/}
          {/*      {missingWorkers.slice(0, 5).map((initial, idx) => (*/}
          {/*        <Avatar*/}
          {/*          key={idx}*/}
          {/*          sx={{*/}
          {/*            width: 24,*/}
          {/*            height: 24,*/}
          {/*            fontSize: "12px",*/}
          {/*            fontFamily: "Roboto",*/}
          {/*            fontWeight: 500,*/}
          {/*            backgroundColor: "#f0f0f0",*/}
          {/*            color: "#000",*/}
          {/*            border: "1px solid #ccc",*/}
          {/*            position: "relative",*/}
          {/*            zIndex: 10 - idx,*/}
          {/*            ml: idx === 0 ? 0 : "-6px",*/}
          {/*          }}*/}
          {/*        >*/}
          {/*          {initial}*/}
          {/*        </Avatar>*/}
          {/*      ))}*/}
          {/*      {missingWorkers.length > 5 && (*/}
          {/*        <Box*/}
          {/*          sx={{*/}
          {/*            width: 24,*/}
          {/*            height: 24,*/}
          {/*            display: "flex",*/}
          {/*            alignItems: "center",*/}
          {/*            justifyContent: "center",*/}
          {/*            fontSize: "12px",*/}
          {/*            fontFamily: "Roboto",*/}
          {/*            color: "rgba(0,0,0,0.6)",*/}
          {/*            zIndex: 0,*/}
          {/*          }}*/}
          {/*        >*/}
          {/*          +{missingWorkers.length - 5}*/}
          {/*        </Box>*/}
          {/*      )}*/}
          {/*    </Box>*/}
          {/*  </Box>*/}
          {/*)}*/}
        </Box>
      </Box>
      <ConfirmProvider defaultOptions={defaultConfirmOptions}>
        <ShiftDialog
          openDialog={openDialog}
          closeDialog={() => setOpenDialog(false)}
          shiftPatternName={shift?.shiftPatternName}
          start={fullStart}
          end={end}
          availableWorkers={availableWorkers}
          requiredWorkers={requiredWorkers}
          shiftId={shift?.shiftId}
          workcenterName={workcenterName ?? ""}
          workcenterId={shift?.workcenterId}
          locationName={getLocationName(shift?.locationId)}
          jobsMap={jobsMap}
          locationId={shift?.locationId}
          workcentersMap={workcentersMap}
          shiftDetails={shift}
        />
      </ConfirmProvider>
    </>
  );
};

const HierarchyGroup = ({
  item,
  shiftMetaMap,
  jobsMap,
  workcentersMap,
  handleShiftVisible,
}: {
  item: WorkcenterRow;
  shiftMetaMap: Record<string, ShiftMeta>;
  jobsMap: Record<number, string>;
  workcentersMap?: Record<number, string>;
  handleShiftVisible: (meta: { shiftId: string; workcenterId: number }) => void;
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [canObserve, setCanObserve] = useState(false);
  const [hoveredShift, setHoveredShift] = useState<{
    patternName: string;
    shiftId: string;
  } | null>(null);

  const totalShifts = Object.values(item.dailyShifts).reduce(
    (acc, shifts) => acc + shifts.length,
    0
  );

  useEffect(() => {
    if (totalShifts === 0) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [totalShifts]);

  const patternEntries = Object.entries(groupShiftsByPatternAcrossDays(item.dailyShifts));

  const sortedPatterns = patternEntries.sort(([_, a], [__, b]) => {
    const getEarliestTimeOfDay = (pattern: typeof a) => {
      return Math.min(
        ...Object.values(pattern.shiftsByDay).map((s) => {
          const d = new Date(s.startDateTime);
          return d.getHours() * 60 + d.getMinutes(); // total minutes since midnight
        })
      );
    };

    const aEarliestTime = getEarliestTimeOfDay(a);
    const bEarliestTime = getEarliestTimeOfDay(b);

    return aEarliestTime - bEarliestTime;
  });

  return (
    <Box
      width="100%"
      sx={{
        backgroundColor: "#fff",
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 64,
          zIndex: 99, // Ensure it's above other content
          backgroundColor: "#e3e6ed",
          borderTop: `1px solid ${BORDER_COLOR}`,
          px: 2,
          py: 1,
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
        }}
        onClick={() => {
          if (totalShifts === 0) return;
          setOpen((prev) => !prev);
        }}
      >
        <Box
          sx={{ width: 24, height: 24, display: "flex", alignItems: "center" }}
        >
          {open ? (
            <KeyboardArrowUpIcon sx={{ width: 24, height: 24 }} />
          ) : (
            <KeyboardArrowDownIcon
              sx={{
                width: 24,
                height: 24,
                opacity: totalShifts === 0 ? 0.5 : 1,
              }}
            />
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-end",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto",
              color: "rgba(0,0,0,0.87)",
              fontSize: "20px",
              fontWeight: 500,
              lineHeight: "32px",
              marginLeft: "4px",
            }}
          >
            {item.workcenterName}
          </Typography>
          <Typography
            sx={{
              fontFamily: "Roboto",
              fontSize: "13px",
              fontWeight: 400,
              lineHeight: "28px",
              color: totalShifts === 0 ? "#6c757d" : "rgba(0,0,0,0.87)",
              marginLeft: "8px",
            }}
          >
            {totalShifts > 1
              ? t("full-time-shifts_shift-count-other", { count: totalShifts })
              : totalShifts === 0
              ? t("full-time-shifts_no-shifts")
              : t("full-time-shifts_shift-count-one", { count: totalShifts })}
          </Typography>
        </Box>
      </Box>

      <Collapse
        in={open}
        timeout="auto"
        unmountOnExit
        onEntered={() => setCanObserve(true)}
      >
        <Grid2
          container
          width="100%"
          sx={{
            paddingTop: "8px",
            paddingBottom: "16px",
            borderBottom: `1px solid ${BORDER_COLOR}`,
          }}
        >
          {sortedPatterns.map(([patternName, { shiftsByDay }], rowIndex) => (
            <Grid2
              container
              key={patternName}
              sx={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                width: "100%",
              }}
            >
              {Array.from({ length: 7 }).map((_, dayIndex) => (
                <Grid2
                  key={dayIndex}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    px: 1,
                    gap: "8px",
                    borderRight: dayIndex !== 6 ? `1px solid ${BORDER_COLOR}` : "none",
                    paddingTop: rowIndex === 0 ? "0px" : "8px",
                  }}
                >
                  {shiftsByDay[dayIndex] && (
                    <ShiftCard
                      shift={shiftsByDay[dayIndex]}
                      shiftMeta={
                        shiftMetaMap[shiftsByDay[dayIndex].shiftId + "_" + shiftsByDay[dayIndex].workcenterId]
                      }
                      jobsMap={jobsMap}
                      workcentersMap={workcentersMap}
                      workcenterName={item.workcenterName}
                      onVisible={handleShiftVisible}
                      observeOnMount={canObserve}
                      hoveredShift={hoveredShift}
                      setHoveredShift={setHoveredShift}
                    />
                  )}
                </Grid2>
              ))}
            </Grid2>
          ))}
        </Grid2>
      </Collapse>
    </Box>
  );
};

const WeeklyShiftView = ({
  workcenters,
  shifts,
  shiftMetaMap,
  jobsMap,
  workcentersMap,
  startDate,
}: {
  workcenters: Workcenter[];
  shifts: ShiftDetail[];
  shiftMetaMap: Record<string, ShiftMeta>;
  jobsMap: Record<number, string>;
  workcentersMap: Record<string, string>;
  startDate: Date;
}) => {
  const dispatch = useDispatch();
  const shiftMetaList = useSelector(
    (state: any) => state.fullTimeShifts.shiftMetaList
  );
  const groupedData = groupShiftsByWorkcenterAndDay(
    workcenters,
    shifts,
    startDate
  );
  const [visibleShiftSet] = useState(() => new Set<string>());
  const [pendingShifts, setPendingShifts] = useState<
    { shiftId: string; workcenterId: number }[]
  >([]);
  const handleShiftVisible = useCallback(
    (meta: { shiftId: string; workcenterId: number }) => {
      const key = `${meta.shiftId}_${meta.workcenterId}`;
      if (!visibleShiftSet.has(key)) {
        visibleShiftSet.add(key);
        setPendingShifts((prev) => [...prev, meta]);
      }
    },
    [visibleShiftSet]
  );
  useEffect(() => {
    if (pendingShifts.length === 0) return;

    const debounce = setTimeout(() => {
      const existingKeys = new Set(
        shiftMetaList.map(
          (meta: { shiftId: string; workcenterId: number }) =>
            `${meta.shiftId}_${meta.workcenterId}`
        )
      );

      const newShifts = pendingShifts.filter(
        ({ shiftId, workcenterId }) =>
          !existingKeys.has(`${shiftId}_${workcenterId}`)
      );

      if (newShifts.length > 0) {
        dispatch(getFullTimeShiftsMeta(newShifts));
      }

      setPendingShifts([]);
    }, 300); // debounce

    return () => clearTimeout(debounce);
  }, [dispatch, pendingShifts, shiftMetaList]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        width: "100%",
      }}
    >
      {groupedData.map((group, i) => (
        <HierarchyGroup
          key={i}
          item={group}
          shiftMetaMap={shiftMetaMap}
          jobsMap={jobsMap}
          workcentersMap={workcentersMap}
          handleShiftVisible={handleShiftVisible}
        />
      ))}
    </Box>
  );
};

export default WeeklyShiftView;
